import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import { useRouter } from "next/router";
import { UserContext } from "../../context";
import Link from "next/link";

// Planet component reused from Assistant.tsx
const Planet = ({
  type,
  style,
}: {
  type: string;
  style: React.CSSProperties;
}) => {
  const planetSrc = `/assets/onboarding/planet_${type}.svg`;

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        ...style,
        transition: "all 0.5s ease",
      }}
    >
      <Image
        src={planetSrc}
        alt={`Planet ${type}`}
        className="w-full h-full object-contain"
        width={100}
        height={100}
      />
    </div>
  );
};

// Custom planet component with CSS-based design
const CustomPlanet = ({
  type = "gas-giant", // gas-giant, rocky, ringed, ice
  size = "md", // sm, md, lg
  color = "blue", // blue, purple, orange, teal, pink
  style,
}: {
  type?: string;
  size?: string;
  color?: string;
  style?: React.CSSProperties;
}) => {
  // Size mapping
  const sizeMap = {
    sm: { width: "20px", height: "20px" },
    md: { width: "35px", height: "35px" },
    lg: { width: "50px", height: "50px" },
  };

  // Color mapping for gradients
  const colorMap = {
    blue: {
      primary: "from-gray-700 to-gray-900",
      secondary: "bg-gray-600",
      glow: "rgba(75, 85, 99, 0.5)",
    },
    purple: {
      primary: "from-gray-700 to-gray-900",
      secondary: "bg-gray-600",
      glow: "rgba(75, 85, 99, 0.5)",
    },
    orange: {
      primary: "from-gray-700 to-gray-900",
      secondary: "bg-gray-600",
      glow: "rgba(75, 85, 99, 0.5)",
    },
    teal: {
      primary: "from-gray-700 to-gray-900",
      secondary: "bg-gray-600",
      glow: "rgba(75, 85, 99, 0.5)",
    },
    pink: {
      primary: "from-gray-700 to-gray-900",
      secondary: "bg-gray-600",
      glow: "rgba(75, 85, 99, 0.5)",
    },
  };

  // Get color values
  const colorValues = colorMap[color] || colorMap.blue;

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        ...sizeMap[size],
        ...style,
        filter: `drop-shadow(0 0 8px ${colorValues.glow})`,
      }}
    >
      {type === "gas-giant" && (
        <div
          className={`w-full h-full rounded-full bg-gradient-to-br ${colorValues.primary} relative overflow-hidden`}
        >
          {/* Cloud bands */}
          <div className="absolute top-1/4 w-full h-[2px] bg-white/20"></div>
          <div className="absolute top-2/4 w-full h-[3px] bg-white/10"></div>
          <div className="absolute top-3/4 w-full h-[2px] bg-white/20"></div>

          {/* Storm spot */}
          <div className="absolute top-[60%] left-[20%] w-[30%] h-[15%] rounded-full bg-white/30 blur-[1px]"></div>
        </div>
      )}

      {type === "rocky" && (
        <div
          className={`w-full h-full rounded-full bg-gradient-to-br ${colorValues.primary} relative overflow-hidden`}
        >
          {/* Craters */}
          <div className="absolute top-[20%] left-[20%] w-[15%] h-[15%] rounded-full bg-white/20 blur-[0.5px]"></div>
          <div className="absolute top-[50%] left-[60%] w-[20%] h-[20%] rounded-full bg-white/20 blur-[0.5px]"></div>
          <div className="absolute top-[70%] left-[30%] w-[10%] h-[10%] rounded-full bg-white/20 blur-[0.5px]"></div>
        </div>
      )}

      {type === "ringed" && (
        <div className="w-full h-full relative">
          {/* Planet body */}
          <div
            className={`w-[80%] h-[80%] absolute top-[10%] left-[10%] rounded-full bg-gradient-to-br ${colorValues.primary}`}
          ></div>

          {/* Rings */}
          <div className="absolute top-[45%] left-0 w-full h-[10%] bg-gradient-to-r from-transparent via-white/30 to-transparent transform -rotate-12"></div>
          <div className="absolute top-[48%] left-0 w-full h-[4%] bg-gradient-to-r from-transparent via-white/20 to-transparent transform -rotate-12"></div>
        </div>
      )}

      {type === "ice" && (
        <div
          className={`w-full h-full rounded-full bg-gradient-to-br ${colorValues.primary} relative overflow-hidden`}
        >
          {/* Ice caps */}
          <div className="absolute top-0 left-[15%] w-[70%] h-[20%] rounded-b-full bg-white/40"></div>
          <div className="absolute bottom-0 left-[15%] w-[70%] h-[20%] rounded-t-full bg-white/40"></div>

          {/* Ice cracks */}
          <div className="absolute top-[40%] left-0 w-full h-[1px] bg-white/30"></div>
          <div className="absolute top-[60%] left-0 w-full h-[1px] bg-white/30"></div>
        </div>
      )}
    </div>
  );
};

// BlazeSessionConfirmation component for displaying session booked confirmation
export const BlazeSessionConfirmation = ({
  sessionId,
  topic,
}: {
  sessionId: string;
  topic: string;
}) => {
  return (
    <div className="bg-gradient-to-br from-black/90 to-gray-900/90 p-5 rounded-xl border border-gray-700/50 shadow-lg relative overflow-hidden">
      {/* Stars background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <span
            key={i}
            className="absolute animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              opacity: Math.random() * 0.7 + 0.3,
              backgroundColor: "white",
              borderRadius: "50%",
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}
      </div>

      {/* Nebula effects */}
      <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full bg-gradient-to-tr from-indigo-500/10 via-purple-500/10 to-transparent blur-xl"></div>
      <div className="absolute top-10 left-0 w-32 h-32 rounded-full bg-gradient-to-tr from-blue-500/10 via-cyan-500/10 to-transparent blur-xl"></div>

      {/* Shooting star animation */}
      <div className="absolute top-5 right-10 w-20 h-[1px] bg-gradient-to-r from-transparent via-white to-transparent transform -rotate-45">
        <motion.div
          className="absolute top-0 left-0 w-1 h-1 bg-white rounded-full"
          animate={{
            left: ["0%", "100%"],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 5,
            ease: "easeOut",
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Header with rocket icon */}
        <div className="flex items-center mb-4">
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 w-8 h-8 rounded-full flex items-center justify-center mr-3 shadow-lg shadow-indigo-500/20">
            <motion.div
              animate={{ rotate: [0, 10, 0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            >
              <span className="text-base" role="img" aria-label="rocket">
                🚀
              </span>
            </motion.div>
          </div>
          <h2 className="text-base font-bold text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-100 to-white">
            Blaze Session Created!
          </h2>
        </div>

        {/* Session details */}
        <div className="ml-11 mb-4">
          <p className="text-sm text-gray-300 mb-2">
            Your Blaze session with a tutor is ready to begin!
          </p>

          {/* Session details section */}
          <div className="bg-gradient-to-r from-gray-800/50 to-transparent p-3 rounded-lg border-l-2 border-indigo-500/50 mb-3">
            <h3 className="text-indigo-300 text-xs font-medium mb-2 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              SESSION DETAILS
            </h3>
            <div className="space-y-2 ml-1">
              <div className="flex">
                <span className="text-gray-400 text-xs w-16">Topic:</span>
                <span className="text-white font-medium text-xs">{topic}</span>
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-300 mb-3">
            A tutor will join your session shortly. Get ready for an
            enlightening learning experience! ✨
          </p>
        </div>

        {/* Action button */}
        <div className="ml-11">
          <Link
            href={`/blaze/chat/${sessionId}`}
            className="inline-flex items-center bg-gradient-to-r from-indigo-500/20 to-purple-600/20 hover:from-indigo-500/30 hover:to-purple-600/30 text-white px-3 py-1.5 rounded-lg border border-indigo-500/30 transition-all duration-300 group text-sm"
          >
            <span className="mr-2">✨</span>
            <span>Enter your Blaze session</span>
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3 ml-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              animate={{ x: [0, 4, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </motion.svg>
          </Link>
        </div>

        {/* Floating planet decoration */}
        <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [0, 10, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            <CustomPlanet
              type="ringed"
              color="purple"
              size="sm"
              style={{ opacity: 0.7 }}
            />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Define the types for our booking flow
type BookingStep =
  | "initial"
  | "category"
  | "subject"
  | "chapter"
  | "details"
  | "submitting";
type Category = string;
type Subject = string;

interface Chapter {
  id: string;
  name: string;
}

interface FeedbackUIProps {
  showFeedbackFor: string | null;
  setShowFeedbackFor: (id: string | null) => void;
  addAssistantResponseMutate: any;
  createBlazeSession: (query: string) => void;
  getLastUserQuery: () => string;
  lastMessageType?: string | null;
  sessionRequestData?: {
    category_name?: string;
    subject_name?: string;
    chapter_name?: string;
    initial_doubt_description?: string;
    grade?: string;
  };
  // New properties for buddy switching
  selectedBuddyId?: string | null;
  handleSelectBuddy?: (buddyId: string) => void;
  // This component handles both show_feedback_ui=true messages
  // and response_type="blaze_session_request" messages
}

export default function FeedbackUI({
  showFeedbackFor,
  setShowFeedbackFor,
  addAssistantResponseMutate,
  createBlazeSession,
  getLastUserQuery,
  lastMessageType,
  sessionRequestData,
  selectedBuddyId, // New prop
  handleSelectBuddy, // New prop
}: FeedbackUIProps) {
  const router = useRouter();
  const [isConnecting, setIsConnecting] = useState(false);
  const [bookingStep, setBookingStep] = useState<BookingStep>(
    sessionRequestData ? "category" : "initial"
  );
  const [user] = useContext(UserContext).user;
  const feedbackRef = useRef<HTMLDivElement>(null);

  // New state for buddy switching UI
  const [showBuddySwitcher, setShowBuddySwitcher] = useState(false);

  // Add state for buddy switcher navigation
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const buddyScrollRef = useRef<HTMLDivElement>(null);

  // Add state to track if this is a buddy recommendation
  const [isBuddyRecommendation, setIsBuddyRecommendation] = useState(false);
  const [recommendedBuddyId, setRecommendedBuddyId] = useState<string | null>(
    null
  );
  const [buddyRecommendationData, setBuddyRecommendationData] = useState<{
    explanation?: string;
    suggestion?: string;
    switch_prompt?: string;
  } | null>(null);

  // States for the booking flow
  const [selectedCategory, setSelectedCategory] = useState<Category | "">(
    sessionRequestData?.category_name || ""
  );
  const [selectedSubject, setSelectedSubject] = useState<Subject | "">(
    sessionRequestData?.subject_name || ""
  );
  const [selectedChapter, setSelectedChapter] = useState<Chapter | null>(null);
  const [doubtDetails, setDoubtDetails] = useState(
    sessionRequestData?.initial_doubt_description
      ? String(sessionRequestData.initial_doubt_description).trim()
      : ""
  );
  const [image, setImage] = useState<{
    url: string;
    ext: string;
    name: string;
    file: File;
  } | null>(null);

  // Add effect to detect buddy recommendations
  useEffect(() => {
    if (showFeedbackFor) {
      // Get the message data from DOM or other source
      // For now, we'll listen for a custom event or check if specific data exists
      const checkForBuddyRecommendation = () => {
        // Try to get the message data from a global context or event
        if (typeof window !== "undefined") {
          const messageData = (window as any).lastBuddyRecommendation;
          if (
            messageData &&
            messageData.response_type === "buddy_recommendation"
          ) {
            setIsBuddyRecommendation(true);
            setRecommendedBuddyId(messageData.recommended_buddy || null);
            setBuddyRecommendationData({
              explanation: messageData.explanation,
              suggestion: messageData.suggestion,
              switch_prompt: messageData.switch_prompt,
            });
            setShowBuddySwitcher(true); // Auto-show buddy switcher for recommendations

            // Clear the data
            delete (window as any).lastBuddyRecommendation;
          }
        }
      };

      checkForBuddyRecommendation();
    }
  }, [showFeedbackFor]);

  // Reset buddy recommendation state when feedback UI is closed
  useEffect(() => {
    if (!showFeedbackFor) {
      setIsBuddyRecommendation(false);
      setRecommendedBuddyId(null);
      setBuddyRecommendationData(null);
      setShowBuddySwitcher(false);
    }
  }, [showFeedbackFor]);

  // If sessionRequestData is provided, auto-advance to the appropriate step
  useEffect(() => {
    if (sessionRequestData && bookingStep === "category") {
      // We'll let the data fetching complete before attempting to match and select items
      console.log("Session request data received:", sessionRequestData);

      // Don't auto-advance yet, we'll do that after matching with API data
      // The matchSuggestedValues function will be called after data is loaded
    }
  }, [sessionRequestData, bookingStep]);

  // Data state
  const [categories, setCategories] = useState<string[]>([]);
  const [subjects, setSubjects] = useState<Record<string, string[]>>({});
  const [chapters, setChapters] = useState<Record<string, Chapter[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userGrade, setUserGrade] = useState<string | null>(null);
  const [subjectColors, setSubjectColors] = useState<Record<string, string>>(
    {}
  );
  const [allowNext, setAllowNext] = useState(false);
  const [userWentBack, setUserWentBack] = useState(false); // Track when user manually goes back
  const [aiSuggestionsApplied, setAiSuggestionsApplied] = useState(false); // Track if AI suggestions have been applied

  // Function to find closest match in an array of strings
  const findBestMatch = (
    suggested: string,
    availableOptions: string[]
  ): string | null => {
    if (!suggested || !availableOptions || availableOptions.length === 0)
      return null;

    // First try exact match (case insensitive)
    const exactMatch = availableOptions.find(
      (option) => option.toLowerCase() === suggested.toLowerCase()
    );
    if (exactMatch) return exactMatch;

    // Then try includes match (case insensitive)
    const includesMatch = availableOptions.find(
      (option) =>
        option.toLowerCase().includes(suggested.toLowerCase()) ||
        suggested.toLowerCase().includes(option.toLowerCase())
    );
    if (includesMatch) return includesMatch;

    // No match found
    return null;
  };

  // Function to find closest chapter match
  const findBestChapterMatch = (
    suggested: string,
    availableChapters: Chapter[]
  ): Chapter | null => {
    if (!suggested || !availableChapters || availableChapters.length === 0)
      return null;

    // First try exact name match
    const exactMatch = availableChapters.find(
      (chapter) => chapter.name.toLowerCase() === suggested.toLowerCase()
    );
    if (exactMatch) return exactMatch;

    // Then try includes match
    const includesMatch = availableChapters.find(
      (chapter) =>
        chapter.name.toLowerCase().includes(suggested.toLowerCase()) ||
        suggested.toLowerCase().includes(chapter.name.toLowerCase())
    );
    if (includesMatch) return includesMatch;

    // No match found
    return null;
  };

  // Match AI suggested values with available API data
  const matchSuggestedValues = useCallback(() => {
    if (!sessionRequestData) return;

    // Don't auto-advance if user manually went back
    if (userWentBack) {
      return;
    }

    // Don't apply suggestions if they've already been applied
    if (aiSuggestionsApplied) {
      return;
    }

    console.log(
      "Matching suggested values with API data - ",
      subjects,
      chapters,
      categories
    );

    // Try to match category
    if (sessionRequestData.category_name && categories.length > 0) {
      const matchedCategory = findBestMatch(
        sessionRequestData.category_name,
        categories
      );
      if (matchedCategory) {
        console.log(`Matched category: ${matchedCategory}`);
        setSelectedCategory(matchedCategory);

        // If we have a matched category, try to match subject
        if (sessionRequestData.subject_name && subjects[matchedCategory]) {
          let matchedSubject = findBestMatch(
            sessionRequestData.subject_name,
            subjects[matchedCategory]
          );

          if (matchedCategory.includes("Math")) {
            matchedSubject = "Maths";
          }

          if (matchedSubject) {
            console.log(`Matched subject: ${matchedSubject}`);
            setSelectedSubject(matchedSubject);

            // If we have a matched subject, try to match chapter
            if (sessionRequestData.chapter_name && chapters[matchedSubject]) {
              const matchedChapter = findBestChapterMatch(
                sessionRequestData.chapter_name,
                chapters[matchedSubject]
              );
              if (matchedChapter) {
                console.log(`Matched chapter: ${matchedChapter.name}`);
                setSelectedChapter(matchedChapter);
              }
            }
          }
        }
      }
    }

    // Always set the doubt description if available
    if (sessionRequestData.initial_doubt_description) {
      const doubtText = String(
        sessionRequestData.initial_doubt_description
      ).trim();
      if (doubtText && doubtText !== "null" && doubtText !== "undefined") {
        setDoubtDetails(doubtText);
      }
    }

    // Mark that AI suggestions have been applied
    setAiSuggestionsApplied(true);

    // Don't automatically advance the booking step - let user navigate with buttons
  }, [
    sessionRequestData,
    categories,
    subjects,
    chapters,
    findBestMatch,
    findBestChapterMatch,
    userWentBack,
    aiSuggestionsApplied,
  ]);

  // Fetch user grade and initialize data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (user) {
          const db = require("../../firebase-config").db;

          // Set user grade from context
          setUserGrade(user.grade || "Class 10");

          // // Fetch subject colors
          // const subjectColorsMeta = async ({ grade }) => {
          //   const docRef = db
          //     .collection("blaze_dev")
          //     .doc("collections")
          //     .collection("subject_colors")
          //     .doc(grade);
          //   const doc = await docRef.get();
          //   if (doc.exists) {
          //     return doc.data();
          //   }
          //   return {};
          // };

          // const colors = await subjectColorsMeta({
          //   grade: user.grade || "Class 10",
          // });
          // setSubjectColors(colors);
        } else {
          setUserGrade("Class 10"); // Default for guest users
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setUserGrade("Class 10"); // Default on error
      }
    };

    fetchUserData();
  }, [user]);

  // Fetch class chapters and categories
  useEffect(() => {
    const fetchClassData = async () => {
      if (!userGrade) return;

      setLoading(true);
      setError(null);

      try {
        const db = require("../../firebase-config").db;
        const docRef = db
          .collection("blaze_dev")
          .doc("collections")
          .collection("ordered_cms_hierarchy")
          .doc(userGrade);

        const doc = await docRef.get();

        if (doc.exists) {
          const data = doc.data();

          // Extract categories from the ordered_cms_hierarchy
          if (data.categories && Array.isArray(data.categories)) {
            setCategories(data.categories);
          } else {
            // Fallback categories
            setCategories(["Maths", "Science", "SST", "English", "General"]);
          }

          // Extract subject_chapter_map for later use
          if (data.subjects) {
            setSubjects(data.subjects);
            setChapters(data.subject_chapter_map);
          }

          if (data.subject_color_map) {
            setSubjectColors(data.subject_color_map);
          }
        } else {
          setError("No data found for your grade");
          // Set fallback data
          setCategories(["Maths", "Science", "SST", "English", "General"]);
        }
      } catch (error) {
        console.error("Error fetching class data:", error);
        setError("Failed to load data");
        // Set fallback data
        setCategories(["Maths", "Science", "SST", "English", "General"]);
      } finally {
        setLoading(false);
      }
    };

    fetchClassData();
  }, [userGrade]);

  // After data is loaded, match the suggested values
  useEffect(() => {
    // Only try to match after categories, subjects, and chapters are loaded
    if (
      sessionRequestData &&
      categories.length > 0 &&
      Object.keys(subjects).length > 0 &&
      Object.keys(chapters).length > 0 &&
      !loading
    ) {
      matchSuggestedValues();
    }
  }, [
    sessionRequestData,
    categories,
    subjects,
    chapters,
    loading,
    matchSuggestedValues,
  ]);

  // Check if next is allowed
  useEffect(() => {
    if (bookingStep === "category") {
      setAllowNext(!!selectedCategory);
    } else if (bookingStep === "subject") {
      setAllowNext(!!selectedSubject);
    } else if (bookingStep === "chapter") {
      // Chapter selection is mandatory
      setAllowNext(!!selectedChapter);
    } else if (bookingStep === "details") {
      setAllowNext(doubtDetails.trim().length >= 10 || !!image);
    }
  }, [
    bookingStep,
    selectedCategory,
    selectedSubject,
    selectedChapter,
    doubtDetails,
    image,
  ]);

  // Scroll to bottom when booking step changes
  useEffect(() => {
    if (bookingStep !== "initial") {
      // Small delay to ensure the UI has updated
      const timer = setTimeout(() => {
        // Try to find the booking-flow-end element first
        const bookingEndElement = document.getElementById("booking-flow-end");
        if (bookingEndElement) {
          bookingEndElement.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        } else if (buddyScrollRef.current) {
          // Fall back to the buddyScrollRef if the end element isn't available
          buddyScrollRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [bookingStep]);

  // Helper function for scrolling to the end of the booking flow
  const scrollToBookingEnd = (delay = 150) => {
    setTimeout(() => {
      const bookingEndElement = document.getElementById("booking-flow-end");
      if (bookingEndElement) {
        bookingEndElement.scrollIntoView({ behavior: "smooth", block: "end" });
      } else if (buddyScrollRef.current) {
        buddyScrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }, delay);
  };

  // Handle direct connect (old behavior)
  const handleConnect = () => {
    setIsConnecting(true);
    // Start the booking flow instead of directly creating a session
    setBookingStep("category");

    // Scroll to the booking flow after a short delay
    scrollToBookingEnd();
  };

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    setUserWentBack(false); // Reset the flag when user makes new selections
    setAiSuggestionsApplied(true); // Prevent AI from overriding user choice
    setSelectedCategory(category);
    setSelectedSubject("");
    setSelectedChapter(null);

    // If Maths is selected, skip subject selection and go directly to chapter selection
    if (category === "Maths" || category === "Mathematics") {
      setBookingStep("chapter");
    } else {
      setBookingStep("subject");
    }

    // Scroll to the end after step change
    scrollToBookingEnd();
  };

  // Handle subject selection
  const handleSubjectSelect = (subject: Subject) => {
    setUserWentBack(false); // Reset the flag when user makes new selections
    setAiSuggestionsApplied(true); // Prevent AI from overriding user choice
    setSelectedSubject(subject);
    setSelectedChapter(null);
    setBookingStep("chapter");

    // Scroll to the end after step change
    scrollToBookingEnd();
  };

  // Handle chapter selection
  const handleChapterSelect = (chapter: Chapter) => {
    setUserWentBack(false); // Reset the flag when user makes new selections
    setAiSuggestionsApplied(true); // Prevent AI from overriding user choice
    setSelectedChapter(chapter);
    setBookingStep("details");

    // Scroll to the end after step change
    scrollToBookingEnd();
  };

  // Handle image upload
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const file = files[0];
      // Create a blob URL for preview and store the original file
      const blobUrl = URL.createObjectURL(file);
      setImage({
        url: blobUrl, // This URL is used by the uploadImage function to fetch the blob
        ext: file.name.split(".").slice(-1)[0],
        name: file.name,
        file: file, // Store the original file for reference if needed
      });
    }
    e.target.value = "";
  };

  // Handle image deletion
  const handleImageDelete = () => {
    setImage(null);
  };

  // Handle doubt details input
  const handleDoubtInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.trim().length <= 350) {
      setDoubtDetails(value);
    }
  };

  // Handle session request submission
  const handleSubmitRequest = async () => {
    setBookingStep("submitting");

    // Scroll to the end after step change
    scrollToBookingEnd();

    try {
      // Get user info from UserContext
      const db = require("../../firebase-config").db;

      if (!user) {
        // Handle guest user case
        router.push({
          pathname: "/blaze",
          query: { topic: getLastUserQuery() },
        });
        return;
      }

      // Create a skill ID based on selection
      let skillId = "";
      if (selectedCategory === "Maths") {
        skillId = userGrade?.toLowerCase().replace(/\s+/g, "_") + "_maths";
      } else if (selectedCategory && selectedSubject) {
        skillId =
          userGrade?.toLowerCase().replace(/\s+/g, "_") +
          "_" +
          selectedCategory.toLowerCase() +
          "_" +
          selectedSubject.replace(/\s+/g, "_").toLowerCase();
      }

      // Get gradient color
      let gradient =
        subjectColors[
          selectedCategory?.startsWith("Math")
            ? selectedCategory
            : selectedSubject || ""
        ];
      if (!gradient) {
        gradient = subjectColors["Exam Prep."] || "";
      }

      // Get student activity data (refund count and session count)
      const getStudentActivity = async (studentId: string) => {
        try {
          const studentDoc = await db
            .collection("blaze_dev")
            .doc("collections")
            .collection("students")
            .doc(studentId)
            .get();

          if (studentDoc.exists) {
            const data = studentDoc.data();
            return {
              refundCount: data?.refund_count || 0,
              sessionCount: data?.session_count || 0,
            };
          } else {
            return {
              refundCount: 0,
              sessionCount: 0,
            };
          }
        } catch (error) {
          console.error("Error getting student activity:", error);
          return {
            refundCount: 0,
            sessionCount: 0,
          };
        }
      };

      const studentActivity = await getStudentActivity(user.uid);

      // Check if user has available doubt count
      const availableDountCount = user.available_doubt_count || 0;
      if (availableDountCount <= 0) {
        // Show error message if no doubt count available
        addAssistantResponseMutate({
          text:
            "You don't have any doubt sessions available. To connect with a tutor, you'll need to purchase doubt sessions by clicking on your profile icon (top right) and then clicking the ADD button under 'Available doubts'. " +
            "Would you like me to continue helping you with this question instead?",
          messageType: "noDoubtSessions",
          metadata: {
            streamComplete: true,
            isStreaming: false,
          },
        });
        setBookingStep("initial");
        setIsConnecting(false);
        setShowFeedbackFor(null);
        return;
      }

      // Prepare request data
      const requestData = {
        skillId,
        studentId: user.uid,
        studentName: user.name,
        profilePic: user.profile_url || null,
        topic: selectedChapter
          ? selectedChapter.name
          : selectedSubject
          ? selectedSubject
          : selectedCategory === "Maths"
          ? "Mathematics"
          : "No Topic",
        topic_cms_path: selectedChapter ? selectedChapter.id : null,
        doubtDetails: doubtDetails,
        images: image
          ? [
              {
                url: image.url,
                name: image.name,
                ext: image.ext,
                file: image.file, // Pass the file object for proper upload
              },
            ]
          : [],
        subject_color_gradient: gradient,
        refundCount: studentActivity.refundCount,
        sessionCount: studentActivity.sessionCount,
      };

      // Import the requestSession function from database/blaze
      const { requestSession } = await import("../../database/blaze");

      const sessionId = await requestSession(requestData);

      if (sessionId) {
        // Get the topic from the selected options
        const topic =
          selectedChapter?.name ||
          selectedSubject ||
          selectedCategory ||
          "General Doubt";

        // Create a message with the blazeSessionConfirmation type
        addAssistantResponseMutate({
          text: "Your Blaze session has been created successfully!",
          messageType: "blazeSessionConfirmation",
          metadata: {
            sessionId: sessionId,
            topic: topic,
            streamComplete: true,
            isStreaming: false,
          },
        });

        // Reset loading states immediately so they don't show with the confirmation
        setIsConnecting(false);
        setBookingStep("initial");
        setShowFeedbackFor(null);

        // Navigate to the chat page after a short delay
        setTimeout(() => {
          router.push(`/blaze/chat/${sessionId}`);
        }, 2000);
      } else {
        // Fallback to the old method
        router.push({
          pathname: "/blaze",
          query: {
            topic:
              selectedChapter?.name ||
              selectedSubject ||
              selectedCategory ||
              getLastUserQuery(),
            details: doubtDetails,
            skillId: skillId,
            topic_cms_path: selectedChapter?.id || null,
          },
        });
      }
    } catch (error) {
      console.error("Error creating Blaze session:", error);

      // Show error message in chat
      addAssistantResponseMutate({
        text:
          "I'm sorry, there was an issue creating your Blaze session. Our team has been notified. " +
          "You can try again or use the Blaze section directly to connect with a tutor.",
        messageType: "blazeSessionError",
        metadata: {
          streamComplete: true,
          isStreaming: false,
        },
      });

      setBookingStep("initial");
      setIsConnecting(false);
      setShowFeedbackFor(null);
    }
  };

  // Cancel booking flow
  const handleCancel = () => {
    if (bookingStep === "initial") {
      setShowFeedbackFor(null);
    } else {
      // Go back to initial state
      setBookingStep("initial");
      setSelectedCategory("");
      setSelectedSubject("");
      setSelectedChapter(null);
      setDoubtDetails("");
      setImage(null);
      setIsConnecting(false);
    }
  };

  // Render loading spinner
  const renderSpinner = (size: "sm" | "md" = "md") => (
    <svg
      className={`animate-spin ${
        size === "sm" ? "h-3 w-3" : "h-4 w-4"
      } text-white`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      ></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );

  // Helper function to get appropriate buddy details
  const getBuddyInfo = (buddyId: string) => {
    const buddiesInfo = {
      // default: { // Commented out - using explainer as default now
      //   name: "AI Helper",
      //   icon: "🤖",
      //   color: "from-blue-600 to-indigo-700",
      //   description: "Your friendly AI assistant",
      // },
      explainer: {
        name: "Concept Explainer",
        icon: "📚",
        color: "from-cyan-500 to-blue-600",
        description: "Detailed explanations of any academic concept",
      },
      blaze: {
        name: "Blaze Sessions",
        icon: "👨‍🏫",
        color: "from-orange-500 to-red-600",
        description: "1-on-1 expert tutoring for any subject",
      },
      live: {
        name: "Class Finder",
        icon: "🎬",
        color: "from-green-500 to-emerald-700",
        description: "Find cool live classes to join",
      },
      search: {
        name: "Video Lectures",
        icon: "🎥",
        color: "from-blue-500 to-indigo-600",
        description: "Watch videos explaining tough topics",
      },
      learning: {
        name: "Study Guide",
        icon: "📝",
        color: "from-purple-500 to-pink-600",
        description: "Step-by-step guides to master any topic",
      },
      settings: {
        name: "Settings Helper",
        icon: "⚙️",
        color: "from-gray-500 to-gray-700",
        description: "Change your theme or grade level",
      },
      // navigation: { // Commented out - removed from UI
      //   name: "Quick Links",
      //   icon: "🚀",
      //   color: "from-amber-500 to-yellow-600",
      //   description: "Jump to different parts of PuStack easily",
      // },
    };

    return buddiesInfo[buddyId] || buddiesInfo.explainer; // Fallback to explainer instead of default
  };

  // Handle buddy switch
  const handleBuddySwitch = (buddyId: string) => {
    if (handleSelectBuddy) {
      handleSelectBuddy(buddyId);

      // Get the last user query to resend with the new buddy
      const lastUserQuery = getLastUserQuery();

      // Get buddy info
      const buddyInfo = getBuddyInfo(buddyId);

      // Close the feedback UI immediately
      setShowFeedbackFor(null);
      setShowBuddySwitcher(false);

      // If we have a last user query, resend it with the new buddy context after a short delay
      if (lastUserQuery && lastUserQuery.trim()) {
        // Add a brief switching message
        addAssistantResponseMutate({
          text: `Switching to ${buddyInfo.name} mode to better help you with your question...`,
          metadata: {
            selectedBuddyId: buddyId,
            streamComplete: true,
            isStreaming: false,
          },
        });

        // Resend the user's query with the new buddy context after a short delay
        setTimeout(() => {
          // Dispatch the follow-up question event to resend the query
          const event = new CustomEvent("assistantFollowUpQuestion", {
            detail: { question: lastUserQuery },
          });
          window.dispatchEvent(event);
        }, 500); // Small delay to let the switching message appear first
      } else {
        // If no previous query, just show the switching message
        addAssistantResponseMutate({
          text: `I've switched to ${buddyInfo.name} mode. How can I help you?`,
          metadata: {
            selectedBuddyId: buddyId,
            streamComplete: true,
            isStreaming: false,
          },
        });
      }
    }
  };

  // Function to toggle buddy switcher
  const toggleBuddySwitcher = () => {
    setShowBuddySwitcher(!showBuddySwitcher);
  };

  // Add scroll navigation functions for buddy switcher
  const checkScrollButtons = useCallback(() => {
    if (buddyScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = buddyScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollLeft = () => {
    if (buddyScrollRef.current) {
      buddyScrollRef.current.scrollBy({
        left: -150,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (buddyScrollRef.current) {
      buddyScrollRef.current.scrollBy({
        left: 150,
        behavior: "smooth",
      });
    }
  };

  // Add touch swipe support
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canScrollRight) {
      scrollRight();
    }
    if (isRightSwipe && canScrollLeft) {
      scrollLeft();
    }
  };

  // Check scroll buttons when buddy switcher is shown
  useEffect(() => {
    if (showBuddySwitcher && buddyScrollRef.current) {
      checkScrollButtons();
      const scrollContainer = buddyScrollRef.current;
      scrollContainer.addEventListener("scroll", checkScrollButtons);

      // Also check on resize
      const handleResize = () => {
        setTimeout(checkScrollButtons, 100);
      };
      window.addEventListener("resize", handleResize);

      return () => {
        scrollContainer.removeEventListener("scroll", checkScrollButtons);
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [showBuddySwitcher, checkScrollButtons]);

  // Render the initial feedback UI
  const renderInitialUI = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mt-4 p-4 ${
        bookingStep === "initial"
          ? "bg-transparent"
          : "bg-gradient-to-br from-black/90 to-gray-900/90 backdrop-blur-sm border border-gray-700"
      } rounded-lg shadow-lg relative overflow-hidden`}
      ref={buddyScrollRef}
    >
      {/* Animated stars in background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(15)].map((_, i) => (
          <span
            key={i}
            className="absolute animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              opacity: Math.random() * 0.7 + 0.3,
              backgroundColor: "white",
              borderRadius: "50%",
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}

        {/* Add a subtle nebula effect */}
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-tr from-gray-700/20 to-gray-600/10 blur-xl"></div>
        <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full bg-gradient-to-br from-gray-700/20 to-gray-600/10 blur-xl"></div>
      </div>

      <div className="relative z-10">
        <h3 className="text-white font-medium text-sm mb-3">
          {isBuddyRecommendation
            ? "🤖 I found a better buddy for your question!"
            : "How was this response?"}
        </h3>

        {/* Show buddy recommendation explanation if available */}
        {isBuddyRecommendation && buddyRecommendationData?.explanation && (
          <div className="mb-3 p-3 bg-indigo-900/20 border border-indigo-700/30 rounded-lg">
            <p className="text-xs text-indigo-200 leading-relaxed">
              {buddyRecommendationData.explanation}
            </p>
          </div>
        )}

        {/* Buttons Row */}
        <div className="flex gap-2 mb-3">
          <motion.button
            onClick={() => {
              addAssistantResponseMutate({
                text: "Great! I'm glad I could help. Let me know if you have any other questions!",
                metadata: {
                  streamComplete: true,
                  isStreaming: false,
                },
              });
              setShowFeedbackFor(null);
            }}
            className="flex-1 bg-gradient-to-r from-green-700/80 to-green-900/80 text-white text-xs py-2 px-3 rounded-lg border border-green-700/30 hover:border-green-600/50 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Helpful 👍
          </motion.button>

          <motion.button
            onClick={toggleBuddySwitcher}
            className="flex-1 bg-gradient-to-r from-gray-700/80 to-gray-800/80 text-white text-xs py-2 px-3 rounded-lg border border-gray-700/30 hover:border-gray-600/50 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isBuddyRecommendation ? "Choose Buddy 🤖" : "Not Helpful 👎"}
          </motion.button>
        </div>

        {/* Buddy Switcher - Show only when toggled or for buddy recommendations */}
        {showBuddySwitcher && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 bg-gradient-to-r from-gray-800/60 to-gray-900/60 p-4 rounded-lg border border-gray-700/50 relative shadow-lg backdrop-blur-sm"
          >
            {/* Cosmic background with subtle star dots */}
            <div className="absolute inset-0 overflow-hidden opacity-30 pointer-events-none rounded-lg">
              {[...Array(20)].map((_, i) => (
                <span
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: `${Math.random() * 2 + 1}px`,
                    height: `${Math.random() * 2 + 1}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.7 + 0.3,
                  }}
                />
              ))}
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-transparent blur-xl"></div>
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-between mb-3">
                <div className="flex flex-col space-y-1">
                  <p className="text-white text-xs flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-3 h-3 mr-1 text-blue-400"
                    >
                      <path
                        fillRule="evenodd"
                        d="M11.078 2.25c-.917 0-1.699.663-1.85 1.567L9.05 4.889c-.02.12-.115.26-.297.348a7.493 7.493 0 00-.986.57c-.166.115-.334.126-.45.083L6.3 5.508a1.875 1.875 0 00-2.282.819l-.922 1.597a1.875 1.875 0 00.432 2.385l.84.692c.095.078.17.229.154.43a7.598 7.598 0 000 1.139c.015.2-.059.352-.153.43l-.841.692a1.875 1.875 0 00-.432 2.385l.922 1.597a1.875 1.875 0 002.282.818l1.019-.382c.115-.043.283-.031.45.082.312.214.641.405.985.57.182.088.277.228.297.35l.178 1.071c.151.904.933 1.567 1.85 1.567h1.844c.916 0 1.699-.663 1.85-1.567l.178-1.072c.02-.12.114-.26.297-.349.344-.165.673-.356.985-.57.167-.114.335-.125.45-.082l1.02.382a1.875 1.875 0 002.28-.819l.923-1.597a1.875 1.875 0 00-.432-2.385l-.84-.692c-.095-.078-.17-.229-.154-.43a7.614 7.614 0 000-1.139c-.016-.2.059-.352.153-.43l.84-.692c.708-.582.891-1.59.433-2.385l-.922-1.597a1.875 1.875 0 00-2.282-.818l-1.02.382c-.114.043-.282.031-.449-.083a7.49 7.49 0 00-.985-.57c-.183-.087-.277-.227-.297-.348l-.179-1.072a1.875 1.875 0 00-1.85-1.567h-1.843zM12 15.75a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isBuddyRecommendation
                      ? `I recommend switching to ${
                          recommendedBuddyId
                            ? getBuddyInfo(recommendedBuddyId).name
                            : "a different buddy"
                        }:`
                      : "Try a different buddy for a better answer:"}
                  </p>
                  {selectedBuddyId && (
                    <span className="text-[10px] text-blue-300 bg-blue-900/30 px-2 py-0.5 rounded-full border border-blue-700/30 self-start">
                      Current: {getBuddyInfo(selectedBuddyId).name}
                    </span>
                  )}
                </div>
              </div>

              {/* Buddy selection cards with consistent sizing */}
              <div className="relative isolate">
                <div
                  ref={buddyScrollRef}
                  className="grid grid-cols-3 grid-rows-2 gap-1 pb-1 hide-scrollbar"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  {[
                    "explainer", // Primary buddy - handles factual questions and explanations
                    "learning",
                    "blaze",
                    "live",
                    "search",
                    "settings",
                  ].map((buddyId) => {
                    const buddy = getBuddyInfo(buddyId);
                    const isSelected = selectedBuddyId === buddyId;
                    const isRecommended = recommendedBuddyId === buddyId;

                    return (
                      <div
                        key={buddyId}
                        className={`flex-shrink-0 h-[77px] md:h-[88px] w-full flex items-center justify-center rounded-lg pt-[4px] ${
                          isSelected
                            ? "bg-gradient-to-b from-gray-700/40 to-transparent"
                            : isRecommended && isBuddyRecommendation
                            ? "bg-gradient-to-b from-indigo-700/40 to-transparent ring-1 ring-indigo-500/50"
                            : "hover:bg-gray-700/20"
                        } transition-colors duration-200`}
                      >
                        <motion.div
                          className={`flex flex-col items-center justify-start cursor-pointer h-full w-full ${
                            isSelected
                              ? "opacity-100"
                              : "opacity-90 hover:opacity-100"
                          }`}
                          whileHover={{ scale: 1.01 }}
                          whileTap={{ scale: 0.99 }}
                          onClick={() => handleBuddySwitch(buddyId)}
                        >
                          <div className="relative mb-1 md:mb-1.5 flex items-center justify-center">
                            <div
                              className={`w-9 h-9 md:w-11 md:h-11 rounded-full bg-gradient-to-br ${
                                buddy.color
                              } flex items-center justify-center shadow-lg ${
                                isSelected
                                  ? "ring-2 ring-white/80"
                                  : isRecommended && isBuddyRecommendation
                                  ? "ring-2 ring-indigo-400/80"
                                  : "hover:ring-1 hover:ring-white/50"
                              }`}
                              style={{
                                boxShadow: isSelected
                                  ? `0 0 12px rgba(255, 255, 255, 0.3), 0 0 4px ${
                                      buddy.color.includes("blue")
                                        ? "rgba(59, 130, 246, 0.5)"
                                        : buddy.color.includes("purple")
                                        ? "rgba(139, 92, 246, 0.5)"
                                        : buddy.color.includes("green")
                                        ? "rgba(34, 197, 94, 0.5)"
                                        : "rgba(255, 255, 255, 0.2)"
                                    }`
                                  : isRecommended && isBuddyRecommendation
                                  ? "0 0 12px rgba(99, 102, 241, 0.4), 0 0 4px rgba(99, 102, 241, 0.6)"
                                  : "none",
                              }}
                            >
                              <span className="text-base md:text-lg">
                                {buddy.icon}
                              </span>
                              {/* Add a subtle pulse effect for selected buddy */}
                              {isSelected && (
                                <motion.div
                                  className="absolute inset-0 rounded-full border border-white/40"
                                  animate={{
                                    scale: [1, 1.15, 1],
                                    opacity: [0.7, 0.3, 0.7],
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  }}
                                />
                              )}
                              {/* Add recommendation badge */}
                              {isRecommended && isBuddyRecommendation && (
                                <motion.div
                                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center"
                                  animate={{
                                    scale: [1, 1.2, 1],
                                    rotate: [0, 10, 0],
                                  }}
                                  transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  }}
                                >
                                  <span className="text-[8px]">⭐</span>
                                </motion.div>
                              )}
                            </div>
                          </div>

                          {/* Buddy name text */}
                          <div className="text-center px-1">
                            <h4
                              className={`text-[10px] md:text-[11px] font-medium leading-tight ${
                                isRecommended && isBuddyRecommendation
                                  ? "text-indigo-200"
                                  : "text-white"
                              }`}
                            >
                              {buddy.name}
                            </h4>
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Add suggestion text for buddy recommendations */}
              {isBuddyRecommendation && buddyRecommendationData?.suggestion && (
                <div className="mt-3 pt-3 border-t border-gray-700/30">
                  <p className="text-xs text-gray-400 text-center italic">
                    💡 {buddyRecommendationData.suggestion}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-500/20 to-transparent h-[1px] my-auto"></div>
          <div className="relative flex justify-center">
            <span className="bg-[#161616] px-2 text-gray-400 text-xs">or</span>
          </div>
        </div>

        {/* Cosmic Connect Button */}
        <div className="mt-4">
          <motion.button
            onClick={handleConnect}
            disabled={isConnecting}
            className="relative w-full overflow-hidden group rounded-xl hover:shadow-lg transition-shadow duration-300 cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Keep the existing Connect with Tutor button content */}
            {/* ... existing button content ... */}

            {/* Cosmic background with stars and nebula */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-gray-800/80 to-gray-900/80 rounded-xl overflow-hidden">
              {/* Stars */}
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full bg-white"
                  style={{
                    width: `${Math.random() * 2 + 1}px`,
                    height: `${Math.random() * 2 + 1}px`,
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.7 + 0.3,
                    animation: `twinkle ${Math.random() * 3 + 2}s infinite ${
                      Math.random() * 2
                    }s`,
                  }}
                />
              ))}

              {/* Planets */}
              <motion.div
                className="absolute w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 opacity-60 top-1 left-[15%]"
                style={{ boxShadow: "0 0 10px rgba(75, 85, 99, 0.5)" }}
                animate={{
                  y: [0, -3, 0],
                  rotate: [0, 5, 0],
                }}
              >
                {/* Planet rings */}
                <div className="absolute top-1/2 left-1/2 w-10 h-3 -translate-x-1/2 -translate-y-1/2 border-t border-blue-200/30 rounded-full transform -rotate-12"></div>
              </motion.div>

              <motion.div
                className="absolute w-5 h-5 rounded-full bg-gradient-to-br from-purple-300 to-purple-500 opacity-70 bottom-2 right-[20%]"
                style={{ boxShadow: "0 0 8px rgba(168, 85, 247, 0.5)" }}
                animate={{
                  y: [0, 2, 0],
                  x: [0, -2, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              <motion.div
                className="absolute w-3 h-3 rounded-full bg-gradient-to-br from-orange-300 to-red-500 opacity-60 top-[60%] right-[10%]"
                style={{ boxShadow: "0 0 6px rgba(249, 115, 22, 0.5)" }}
                animate={{
                  scale: [1, 1.1, 1],
                  opacity: [0.6, 0.8, 0.6],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Cosmic dust/nebula */}
              <div className="absolute top-0 left-0 w-full h-full opacity-30">
                <div className="absolute top-1/4 left-1/4 w-1/2 h-1/2 rounded-full bg-purple-500/30 filter blur-xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-1/2 h-1/2 rounded-full bg-blue-500/30 filter blur-xl"></div>
              </div>

              {/* Shooting star - appears on hover */}
              <motion.div
                className="absolute w-0.5 h-12 bg-gradient-to-b from-white via-blue-300 to-transparent -top-10 left-[30%] opacity-0 group-hover:opacity-100 transform -rotate-45 origin-bottom"
                animate={{
                  top: ["-10%", "110%"],
                  left: ["30%", "70%"],
                  opacity: [0, 1, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  repeatDelay: 3,
                  ease: "easeOut",
                }}
              />
            </div>

            {/* Button content container */}
            <div className="relative z-10 py-4 px-4 flex items-center justify-between">
              {/* Left side with icon and text */}
              <div className="flex items-center flex-1">
                {isConnecting ? (
                  <div className="animate-spin h-6 w-6 text-white mr-3 flex-shrink-0">
                    <svg className="h-full w-full" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  <div className="relative mr-3 flex-shrink-0">
                    <div className="absolute inset-0 rounded-full bg-blue-400/30 animate-ping-slow"></div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-blue-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
                      />
                    </svg>
                  </div>
                )}

                <div className="flex flex-col items-start">
                  <span className="text-white font-semibold text-sm drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
                    {isConnecting
                      ? "Connecting to Tutor..."
                      : "Connect with a Tutor"}
                  </span>
                  <span className="text-white text-xs drop-shadow-[0_1px_1px_rgba(0,0,0,0.5)]">
                    Get personalized help now
                  </span>
                </div>
              </div>

              {/* Right side with rocket animation */}
              <div className="relative flex-shrink-0 ml-2">
                {!isConnecting && (
                  <motion.div
                    className="relative"
                    animate={{ y: [0, -2, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    {/* Rocket */}
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-white transform rotate-45"
                    >
                      <path
                        d="M4.5 18.75L12 12.75M12 12.75L19.5 6.75M12 12.75V22.5M12 12.75L19.5 18.75M12 12.75L4.5 6.75M12 12.75V3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>

                    {/* Rocket flames */}
                    <motion.div
                      className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3 h-3 flex justify-center"
                      initial={{ opacity: 0.7, scale: 0.8 }}
                      animate={{
                        opacity: [0.7, 1, 0.7],
                        scale: [0.8, 1.2, 0.8],
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <div className="w-1 h-2 bg-orange-500 rounded-full blur-[1px]"></div>
                    </motion.div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Always visible pulsing border to indicate clickability */}
            <div className="absolute inset-0 rounded-xl border border-blue-400/30 animate-pulse-border"></div>

            {/* Active border to indicate clickability on hover */}
            <div className="absolute inset-0 rounded-xl border-2 border-transparent group-hover:border-blue-400/50 transition-colors duration-300"></div>

            {/* Always visible bottom highlight */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400/40 via-purple-400/40 to-pink-400/40"
              initial={{ width: "30%" }}
              animate={{ width: ["30%", "70%", "30%"] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Hover effect - cosmic trail */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400"
              initial={{ width: "0%" }}
              whileHover={{ width: "100%" }}
              transition={{ duration: 0.3 }}
            />

            {/* Launch effect on hover */}
            <div
              className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 h-0 
                          group-hover:w-full group-hover:h-full 
                          transition-all duration-500 ease-out 
                          bg-gradient-to-t from-blue-500/20 via-indigo-500/10 to-transparent 
                          rounded-xl opacity-0 group-hover:opacity-100"
            ></div>
          </motion.button>
        </div>
      </div>

      {/* Add global styles for animations */}
      <style jsx global>{`
        @keyframes twinkle {
          0%,
          100% {
            opacity: 0.3;
          }
          50% {
            opacity: 1;
          }
        }
        .animate-twinkle {
          animation: twinkle var(--duration, 3s) infinite var(--delay, 0s);
        }
        @keyframes ping-slow {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.3;
          }
          50% {
            transform: scale(1.5);
            opacity: 0.5;
          }
        }
        .animate-ping-slow {
          animation: ping-slow 2s ease-in-out infinite;
        }
        @keyframes orbit {
          0% {
            transform: rotate(0deg) translateX(20px) rotate(0deg);
          }
          100% {
            transform: rotate(360deg) translateX(20px) rotate(-360deg);
          }
        }
        .animate-orbit {
          animation: orbit 10s linear infinite;
        }
        @keyframes pulse-border {
          0%,
          100% {
            border-color: rgba(96, 165, 250, 0.2);
            box-shadow: 0 0 0 rgba(96, 165, 250, 0);
          }
          50% {
            border-color: rgba(96, 165, 250, 0.5);
            box-shadow: 0 0 5px rgba(96, 165, 250, 0.3);
          }
        }
        .animate-pulse-border {
          animation: pulse-border 3s ease-in-out infinite;
        }
        /* Add custom breakpoint for xs screens */
        @media (min-width: 480px) {
          .xs\\:grid-cols-4 {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }
      `}</style>
    </motion.div>
  );

  // Render the booking flow UI with chips
  const renderBookingFlowUI = () => {
    // For Maths, use the category directly as the key to get chapters
    const subjectKey =
      selectedCategory === "Maths" || selectedCategory === "Mathematics"
        ? selectedCategory
        : selectedSubject;
    const availableChapters = subjectKey ? chapters[subjectKey] || [] : [];

    return (
      <div className="space-y-4 relative">
        {/* Animated stars in background */}
        <div className="absolute inset-0 overflow-hidden -z-10">
          {[...Array(10)].map((_, i) => (
            <span
              key={i}
              className="absolute animate-twinkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${Math.random() * 2 + 1}px`,
                height: `${Math.random() * 2 + 1}px`,
                opacity: Math.random() * 0.7 + 0.3,
                backgroundColor: "white",
                borderRadius: "50%",
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${Math.random() * 3 + 2}s`,
              }}
            />
          ))}

          {/* Add a subtle nebula effect */}
          <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-tr from-purple-500/20 to-indigo-500/10 blur-xl"></div>

          {/* Add custom planets to the booking flow UI */}
          <CustomPlanet
            type="gas-giant"
            color="purple"
            size="md"
            style={{
              top: "5%",
              left: "5%",
              opacity: 0.7,
              animation: "float 7s ease-in-out infinite",
            }}
          />

          <CustomPlanet
            type="rocky"
            color="teal"
            size="sm"
            style={{
              bottom: "10%",
              right: "5%",
              opacity: 0.6,
              animation: "float 9s ease-in-out infinite",
            }}
          />
        </div>

        {/* Show AI suggestion notice at the top */}
        {sessionRequestData && (
          <div className="mb-4 p-3 bg-gradient-to-br from-blue-900/30 to-indigo-900/20 rounded-lg border border-blue-700/30">
            <p className="text-sm text-blue-300 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1.5 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              AI Suggested Options
            </p>
            <p className="text-xs text-gray-300">
              I've suggested some options based on your query. You can adjust
              these selections as needed before submitting.
              {((sessionRequestData.category_name && !selectedCategory) ||
                (sessionRequestData.subject_name && !selectedSubject) ||
                (sessionRequestData.chapter_name && !selectedChapter)) && (
                <span className="block mt-1 text-yellow-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 mr-1 inline-block text-yellow-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  Some suggestions didn't match available options exactly. Items
                  with yellow indicators are potential partial matches.
                </span>
              )}
            </p>
          </div>
        )}

        {/* Title with icon - improved contrast */}
        <div className="flex items-center mb-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z"
            />
          </svg>
          <h3 className="text-sm font-medium text-white">
            Connect with a Tutor
          </h3>
        </div>

        {/* Categories section - improved contrast */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-white font-medium">Select a category:</p>
            {sessionRequestData?.category_name && !selectedCategory && (
              <div className="bg-yellow-900/30 border border-yellow-500/30 rounded px-2 py-0.5">
                <p className="text-xs text-yellow-300 italic flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3 w-3 mr-1 text-yellow-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  AI suggested "{sessionRequestData.category_name}" (not found)
                </p>
              </div>
            )}
          </div>
          {loading ? (
            <div className="flex justify-center py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className={`px-2.5 py-1 text-xs rounded-full transition-all duration-200 ${
                    selectedCategory === category
                      ? "bg-gradient-to-r from-indigo-900/90 to-purple-900/90 text-white border border-indigo-500/50 shadow-md shadow-indigo-500/20"
                      : sessionRequestData?.category_name &&
                        category
                          .toLowerCase()
                          .includes(
                            sessionRequestData.category_name.toLowerCase()
                          )
                      ? "bg-black/60 text-white border border-yellow-500/50 hover:bg-gradient-to-r hover:from-indigo-900/40 hover:to-purple-900/40 hover:border-indigo-500/30"
                      : "bg-black/60 text-white border border-gray-700 hover:bg-gradient-to-r hover:from-indigo-900/40 hover:to-purple-900/40 hover:border-indigo-500/30"
                  }`}
                >
                  {category}
                  {sessionRequestData?.category_name &&
                    !selectedCategory &&
                    category
                      .toLowerCase()
                      .includes(
                        sessionRequestData.category_name.toLowerCase()
                      ) && (
                      <span className="ml-1 text-yellow-400 text-xs">•</span>
                    )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Subjects section - only show if category is selected and it's not Maths - improved contrast */}
        {selectedCategory &&
          selectedCategory !== "Maths" &&
          selectedCategory !== "Mathematics" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-white font-medium">
                  Select a subject:
                </p>
                {sessionRequestData?.subject_name && !selectedSubject && (
                  <div className="bg-yellow-900/30 border border-yellow-500/30 rounded px-2 py-0.5">
                    <p className="text-xs text-yellow-300 italic flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 mr-1 text-yellow-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      AI suggested "{sessionRequestData.subject_name}" (not
                      found)
                    </p>
                  </div>
                )}
              </div>
              {loading ? (
                <div className="flex justify-center py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCategory &&
                    subjects[selectedCategory] &&
                    subjects[selectedCategory].map((subject) => (
                      <button
                        key={subject}
                        onClick={() => handleSubjectSelect(subject)}
                        className={`px-2.5 py-1 text-xs rounded-full transition-all duration-200 ${
                          selectedSubject === subject
                            ? "bg-gradient-to-r from-teal-900/90 to-cyan-900/90 text-white border border-teal-500/50 shadow-md shadow-teal-500/20"
                            : sessionRequestData?.subject_name &&
                              subject
                                .toLowerCase()
                                .includes(
                                  sessionRequestData.subject_name.toLowerCase()
                                )
                            ? "bg-black/60 text-white border border-yellow-500/50 hover:bg-gradient-to-r hover:from-teal-900/40 hover:to-cyan-900/40 hover:border-teal-500/30"
                            : "bg-black/60 text-white border border-gray-700 hover:bg-gradient-to-r hover:from-teal-900/40 hover:to-cyan-900/40 hover:border-teal-500/30"
                        }`}
                      >
                        {subject}
                        {sessionRequestData?.subject_name &&
                          !selectedSubject &&
                          subject
                            .toLowerCase()
                            .includes(
                              sessionRequestData.subject_name.toLowerCase()
                            ) && (
                            <span className="ml-1 text-yellow-400 text-xs">
                              •
                            </span>
                          )}
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}

        {/* Chapters section - only show if subject is selected or category is Maths - improved contrast */}
        {(selectedSubject ||
          (selectedCategory &&
            (selectedCategory === "Maths" ||
              selectedCategory === "Mathematics"))) && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-white font-medium">
                Select a chapter:
              </p>
              {sessionRequestData?.chapter_name && !selectedChapter && (
                <div className="bg-yellow-900/30 border border-yellow-500/30 rounded px-2 py-0.5">
                  <p className="text-xs text-yellow-300 italic flex items-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 mr-1 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    AI suggested "{sessionRequestData.chapter_name}" (not found)
                  </p>
                </div>
              )}
            </div>
            {loading ? (
              <div className="flex justify-center py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              </div>
            ) : availableChapters.length > 0 ? (
              <>
                {sessionRequestData?.chapter_name && !selectedChapter && (
                  <div className="mb-3 p-2 bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 rounded-lg border border-yellow-700/30">
                    <p className="text-xs text-yellow-300 flex items-start">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-1.5 text-yellow-400 flex-shrink-0 mt-0.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span>
                        I couldn't find an exact match for "
                        <strong>{sessionRequestData.chapter_name}</strong>".
                        Please select the most relevant chapter from the options
                        below, or you can try switching to another grade which
                        covers this chapter.
                      </span>
                    </p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {availableChapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => handleChapterSelect(chapter)}
                      className={`px-2.5 py-1 text-xs rounded-full transition-all duration-200 ${
                        selectedChapter?.id === chapter.id
                          ? "bg-gradient-to-r from-amber-900/90 to-orange-900/90 text-white border border-amber-500/50 shadow-md shadow-amber-500/20"
                          : sessionRequestData?.chapter_name &&
                            chapter.name
                              .toLowerCase()
                              .includes(
                                sessionRequestData.chapter_name.toLowerCase()
                              )
                          ? "bg-black/60 text-white border border-yellow-500/50 hover:bg-gradient-to-r hover:from-amber-900/40 hover:to-orange-900/40 hover:border-amber-500/30"
                          : "bg-black/60 text-white border border-gray-700 hover:bg-gradient-to-r hover:from-amber-900/40 hover:to-orange-900/40 hover:border-amber-500/30"
                      }`}
                    >
                      {chapter.name}
                      {sessionRequestData?.chapter_name &&
                        !selectedChapter &&
                        chapter.name
                          .toLowerCase()
                          .includes(
                            sessionRequestData.chapter_name.toLowerCase()
                          ) && (
                          <span className="ml-1 text-yellow-400 text-xs">
                            •
                          </span>
                        )}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-xs text-white p-2 border border-gray-700 rounded-lg bg-black/50">
                No chapters found for this subject. Please select a different
                subject or contact support if this seems incorrect.
              </div>
            )}
          </div>
        )}

        {/* Action buttons - improved contrast */}
        <div className="flex justify-between mt-3">
          {/* Only show Back button if this was user-triggered (not AI-triggered) */}
          {!sessionRequestData && (
            <button
              onClick={() => {
                setUserWentBack(true); // Mark that user manually went back
                setBookingStep("category");
                scrollToBookingEnd();
              }}
              className="text-xs bg-black/30 hover:bg-black/50 text-white px-3 py-1.5 rounded-lg transition-all duration-200 border border-gray-700 cursor-pointer"
            >
              Back
            </button>
          )}

          {selectedCategory && (
            <button
              onClick={() => {
                if (
                  selectedCategory &&
                  (selectedSubject ||
                    selectedCategory === "Maths" ||
                    selectedCategory === "Mathematics") &&
                  (bookingStep !== "chapter" || selectedChapter)
                ) {
                  setBookingStep("details");
                  scrollToBookingEnd();
                } else {
                  // If only category is selected, we need subject first
                  // This is handled by the UI flow
                }
              }}
              disabled={
                !(
                  selectedCategory &&
                  (selectedSubject ||
                    selectedCategory === "Maths" ||
                    selectedCategory === "Mathematics") &&
                  (bookingStep !== "chapter" || selectedChapter)
                )
              }
              className={`text-xs px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer ${
                selectedCategory &&
                (selectedSubject ||
                  selectedCategory === "Maths" ||
                  selectedCategory === "Mathematics") &&
                (bookingStep !== "chapter" || selectedChapter)
                  ? "bg-gradient-to-r from-gray-800 to-gray-900 text-white border border-gray-600 hover:from-gray-700 hover:to-gray-800 shadow-sm"
                  : "bg-black/60 text-white/50 border border-gray-700 cursor-not-allowed"
              } ${!sessionRequestData ? "ml-auto" : ""}`}
            >
              Continue
            </button>
          )}
        </div>

        {/* Add a div at the end for scrolling */}
        <div className="h-1 mt-4" id="booking-flow-end"></div>
      </div>
    );
  };

  // Render doubt details UI
  const renderDetailsUI = () => (
    <div className="space-y-4 relative">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden -z-10">
        {/* Animated stars */}
        {[...Array(8)].map((_, i) => (
          <span
            key={i}
            className="absolute animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              opacity: Math.random() * 0.7 + 0.3,
              backgroundColor: "white",
              borderRadius: "50%",
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}

        {/* Nebula effect */}
        <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-tr from-purple-500/20 to-indigo-500/10 blur-xl"></div>
      </div>

      {/* AI suggestion notice if we're using AI suggested doubt */}
      {sessionRequestData?.initial_doubt_description &&
        sessionRequestData.initial_doubt_description !== "null" &&
        sessionRequestData.initial_doubt_description !== "undefined" &&
        String(sessionRequestData.initial_doubt_description).trim() ===
          doubtDetails && (
          <div className="mb-2 p-3 bg-gradient-to-br from-blue-900/30 to-indigo-900/20 rounded-lg border border-blue-700/30">
            <p className="text-sm text-blue-300 mb-1 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1.5 text-blue-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              AI Suggested Doubt
            </p>
            <p className="text-xs text-gray-300">
              I've suggested a doubt description based on your query. Feel free
              to edit it before submitting.
            </p>
          </div>
        )}

      {/* Title */}
      <div className="flex flex-col md:flex-row gap-2 items-start md:items-center justify-between">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
            />
          </svg>
          <h3 className="text-sm font-medium text-white">
            Describe Your Doubt
          </h3>
        </div>

        {/* Path display */}
        <div className="text-xs text-gray-400">
          {selectedCategory}
          {selectedSubject && ` > ${selectedSubject}`}
          {selectedChapter && ` > ${selectedChapter.name}`}
        </div>
      </div>

      {/* Input section */}
      <div className="space-y-3 mt-2">
        <textarea
          placeholder="Please describe your doubt or problem in detail (min. 10 characters)"
          value={doubtDetails}
          onChange={handleDoubtInput}
          className="w-full h-24 bg-black/50 border border-gray-700 rounded-lg text-white text-xs p-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none resize-none"
        />

        {/* Image upload section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs text-white font-medium">
              Add an image (optional)
            </p>
            {image && (
              <button
                onClick={handleImageDelete}
                className="text-xs text-red-400 hover:text-red-300"
              >
                Remove
              </button>
            )}
          </div>

          {image ? (
            <div className="relative rounded-lg overflow-hidden border border-gray-700 h-40">
              <img
                src={image.url}
                alt="Uploaded image"
                className="object-contain w-full h-full"
              />
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-24 bg-black/30 border border-dashed border-gray-700 rounded-lg cursor-pointer hover:bg-black/50 transition-colors">
              <div className="flex flex-col items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-gray-500 mb-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-xs text-gray-500">
                  Click to upload an image
                </p>
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </label>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between mt-3">
        <button
          onClick={() => {
            setUserWentBack(true); // Mark that user manually went back
            setBookingStep("category");
            scrollToBookingEnd();
          }}
          className="text-xs bg-black/30 hover:bg-black/50 text-white px-3 py-1.5 rounded-lg transition-all duration-200 border border-gray-700 cursor-pointer"
        >
          Back
        </button>

        <button
          onClick={handleSubmitRequest}
          disabled={!allowNext || loading}
          className={`text-xs px-3 py-1.5 rounded-lg transition-all duration-200 cursor-pointer flex items-center ${
            allowNext && !loading
              ? "bg-gradient-to-r from-indigo-600 to-blue-700 text-white border border-indigo-500 hover:from-indigo-500 hover:to-blue-600 shadow-md shadow-indigo-500/20"
              : "bg-black/60 text-white/50 border border-gray-700 cursor-not-allowed"
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent mr-1.5"></div>
              Processing...
            </>
          ) : (
            <>Submit Request</>
          )}
        </button>
      </div>

      {/* Add a div at the end for scrolling */}
      <div className="h-1 mt-4" id="booking-flow-end"></div>
    </div>
  );

  // Render submitting UI with enhanced space theme
  const renderSubmittingUI = () => (
    <div className="flex flex-col items-center justify-center py-8 relative">
      {/* Cosmic background with stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Animated stars */}
        {[...Array(25)].map((_, i) => (
          <span
            key={i}
            className="absolute animate-twinkle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: `${Math.random() * 2 + 1}px`,
              height: `${Math.random() * 2 + 1}px`,
              opacity: Math.random() * 0.7 + 0.3,
              backgroundColor: "white",
              borderRadius: "50%",
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${Math.random() * 3 + 2}s`,
            }}
          />
        ))}

        {/* Nebula effects */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[250px] h-[250px] rounded-full bg-gradient-to-r from-indigo-800/10 via-purple-700/10 to-blue-800/10 blur-xl animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-0 w-40 h-40 rounded-full bg-gradient-to-tr from-purple-500/10 via-indigo-500/10 to-transparent blur-xl"></div>

        {/* Shooting stars */}
        <motion.div
          className="absolute w-[2px] h-[40px] bg-gradient-to-b from-white via-blue-300 to-transparent"
          style={{ top: "10%", left: "20%", transform: "rotate(-45deg)" }}
          animate={{
            top: ["10%", "80%"],
            left: ["20%", "60%"],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: 7,
            ease: "easeOut",
          }}
        />
        <motion.div
          className="absolute w-[1px] h-[30px] bg-gradient-to-b from-white via-purple-300 to-transparent"
          style={{ top: "5%", right: "30%", transform: "rotate(-45deg)" }}
          animate={{
            top: ["5%", "70%"],
            right: ["30%", "10%"],
            opacity: [0, 1, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatDelay: 5,
            ease: "easeOut",
            delay: 3,
          }}
        />

        {/* Orbiting planets */}
        <motion.div
          className="absolute"
          animate={{ rotate: 360 }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            width: "150px",
            height: "150px",
            top: "50%",
            left: "50%",
            marginLeft: "-75px",
            marginTop: "-75px",
          }}
        >
          <CustomPlanet
            type="ringed"
            color="purple"
            size="sm"
            style={{
              top: "-15px",
              left: "50%",
              opacity: 0.8,
              transform: "translateX(-50%)",
            }}
          />
        </motion.div>

        <motion.div
          className="absolute"
          animate={{ rotate: -360 }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            width: "200px",
            height: "200px",
            top: "50%",
            left: "50%",
            marginLeft: "-100px",
            marginTop: "-100px",
          }}
        >
          <CustomPlanet
            type="rocky"
            color="blue"
            size="sm"
            style={{
              bottom: "-15px",
              right: "0",
              opacity: 0.7,
            }}
          />
        </motion.div>

        <motion.div
          className="absolute"
          animate={{ rotate: 180 }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            width: "250px",
            height: "250px",
            top: "50%",
            left: "50%",
            marginLeft: "-125px",
            marginTop: "-125px",
          }}
        >
          <CustomPlanet
            type="gas-giant"
            color="teal"
            size="sm"
            style={{
              left: "-10px",
              top: "50%",
              opacity: 0.6,
              transform: "translateY(-50%)",
            }}
          />
        </motion.div>
      </div>

      {/* Central cosmic portal */}
      <div className="relative mb-6">
        {/* Outer ring */}
        <motion.div
          className="w-24 h-24 rounded-full border-4 border-indigo-600/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        >
          {/* Middle ring */}
          <motion.div
            className="absolute inset-1 rounded-full border-2 border-dashed border-purple-500/40"
            animate={{ rotate: -360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          ></motion.div>

          {/* Inner ring with gradient */}
          <div className="absolute inset-[15%] rounded-full bg-gradient-to-br from-black to-gray-900/80 flex items-center justify-center overflow-hidden">
            {/* Pulsing core */}
            <motion.div
              className="w-2/3 h-2/3 rounded-full bg-gradient-to-r from-indigo-700 to-purple-800"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.7, 1, 0.7],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* Particles */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                initial={{
                  x: 0,
                  y: 0,
                }}
                animate={{
                  x: [0, (Math.random() - 0.5) * 15],
                  y: [0, (Math.random() - 0.5) * 15],
                  opacity: [1, 0],
                  scale: [1, 0],
                }}
                transition={{
                  duration: 1 + Math.random(),
                  repeat: Infinity,
                  repeatDelay: Math.random() * 0.5,
                }}
              />
            ))}
          </div>
        </motion.div>

        {/* Orbital paths */}
        <motion.div
          className="absolute -inset-4 rounded-full border border-indigo-600/10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        ></motion.div>

        <motion.div
          className="absolute -inset-8 rounded-full border border-purple-600/5"
          animate={{ rotate: -360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        ></motion.div>

        {/* Rocket ship */}
        <motion.div
          className="absolute -top-8 left-1/2 transform -translate-x-1/2"
          animate={{
            y: [0, -5, 0],
            rotate: [0, 5, 0, -5, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="text-2xl">🚀</div>
          {/* Rocket flames */}
          <motion.div
            className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3 h-3 flex justify-center"
            initial={{ opacity: 0.7, scale: 0.8 }}
            animate={{
              opacity: [0.7, 1, 0.7],
              scale: [0.8, 1.2, 0.8],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <div className="w-1 h-2 bg-orange-500 rounded-full blur-[1px]"></div>
          </motion.div>
        </motion.div>
      </div>

      {/* Text with animated dots */}
      <div className="text-center z-10">
        <p className="text-white font-medium mb-1 text-lg">
          Establishing Blaze Connection
        </p>
        <div className="flex justify-center items-center">
          <motion.span
            className="inline-block w-2 h-2 rounded-full bg-indigo-400 mx-0.5"
            animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
          />
          <motion.span
            className="inline-block w-2 h-2 rounded-full bg-purple-400 mx-0.5"
            animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          />
          <motion.span
            className="inline-block w-2 h-2 rounded-full bg-blue-400 mx-0.5"
            animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
          />
        </div>
        <p className="text-sm text-gray-300 mt-3 max-w-xs text-center">
          Preparing your learning session with a tutor.
        </p>
      </div>

      {/* Add a div at the end for scrolling */}
      <div className="h-8 mt-4" id="booking-flow-end"></div>
    </div>
  );

  // Render the appropriate UI based on the booking step
  const renderBookingUI = () => {
    switch (bookingStep) {
      case "initial":
        return renderInitialUI();
      case "category":
      case "subject":
      case "chapter":
        return renderBookingFlowUI();
      case "details":
        return renderDetailsUI();
      case "submitting":
        return renderSubmittingUI();
      default:
        return renderInitialUI();
    }
  };

  // Don't render anything if there's a session confirmation already
  if (lastMessageType === "blazeSessionConfirmation") {
    return null;
  }

  return <>{renderBookingUI()}</>;
}
